import { Avatar, Box, Card, CardContent, Grid, Typography, CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import CountUp from "react-countup";
import TenantService from "../../services/tenantService";
import TenantLayout from "../../layouts/TenantLayout";
import { tenantPanelContentSx } from "../tenant-permission-groups/tenantPanelContentSx";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import StatsFilters, { ENTITIES } from "../../components/Stats/StatsFilters";
import StatsChart from "../../components/Stats/StatsChart";
import StatsSummaryCards from "../../components/Stats/StatsSummaryCards";
import StatsService from "../../services/doctorServices/StatsService";

const TENANT_ENTITIES = [
  { value: "doctors", label: "Doctors" },
  ...ENTITIES,
];

export default function TenDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(['tenantDashboard', 'stats', 'dashboard']);
  const tenantService = useMemo(() => new TenantService(), []);

  const [adminDoctors, setAdminDoctors] = useState([]);
  const [userDoctors, setUserDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const statsService = useMemo(() => new StatsService(), []);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState(() => TENANT_ENTITIES.map((e) => e.value));
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("last_month");
  const [selectedGroupBy, setSelectedGroupBy] = useState("day");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const token =
    typeof window !== "undefined" ? localStorage.getItem("tokenTenant") : null;

  useEffect(() => {
    const fetchAllDoctors = async () => {
      try {
        const response = await tenantService.getAllDoctor(token);
        const admins = response?.doctors?.admins ?? [];
        setAdminDoctors(admins);

        const formattedDoctors =
          admins
            .flatMap((admin) => admin.normalDoctors || [])
            .filter((doctor) => doctor.role === "doctor") ?? [];

        setUserDoctors(formattedDoctors);
      } catch (error) {
        console.log("Doctors could not be fetched.", error);
        setError(t('errorLoadingDoctors'));
      }
    };

    const fetchAllPatients = async () => {
      try {
        const response = await tenantService.getAllPatients(token);
        setPatients(response?.patients ?? []);
      } catch (error) {
        console.log("Patients could not be fetched.", error);
        setError(t('errorLoadingPatients'));
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    setError(null);
    fetchAllDoctors();
    fetchAllPatients();
  }, [tenantService, token, t]);

  const fetchStatsData = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      let startDate, endDate;
      if (selectedTimeFilter === "custom") {
        startDate = customStartDate || "";
        endDate = customEndDate || "";
        if (!startDate || !endDate) {
          setStatsError("Please select both start and end dates for custom date range");
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
        if (new Date(endDate) < new Date(startDate)) {
          setStatsError("End date must be after start date");
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
      } else {
        const range = statsService.getDateRange(selectedTimeFilter);
        startDate = range.startDate;
        endDate = range.endDate;
      }
      const groupBy =
        selectedGroupBy === "none"
          ? statsService.getSuggestedGroupBy(selectedTimeFilter)
          : selectedGroupBy;
      const entityParam = selectedEntities.length ? selectedEntities : ["patients"];
      const params = {
        entity: Array.isArray(entityParam) ? entityParam.join(",") : entityParam,
        metric: "count",
        start_date: startDate,
        end_date: endDate,
        group_by: groupBy,
      };
      const response = await statsService.getTenantStats(params);
      if (response && response.data) {
        setStatsData(response);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      console.error("Tenant stats error:", err);
      setStatsError(err.response?.data?.message || err.message || "Failed to load stats");
      setStatsData(null);
    } finally {
      setStatsLoading(false);
    }
  }, [
    selectedEntities,
    selectedTimeFilter,
    selectedGroupBy,
    customStartDate,
    customEndDate,
    statsService,
  ]);

  useEffect(() => {
    if (selectedTimeFilter === "custom" && (!customStartDate || !customEndDate)) return;
    fetchStatsData();
  }, [
    selectedEntities,
    selectedTimeFilter,
    selectedGroupBy,
    customStartDate,
    customEndDate,
    fetchStatsData,
  ]);

  const totalDoctors = adminDoctors.length + userDoctors.length;

  const marketingPalette = useMemo(
    () => ({
      hero: `linear-gradient(135deg, ${colors.primary[500]}dd 0%, ${colors.blueAccent[700]}cc 100%)`,
      surface: "#fffffffa",
      accentBorder: `1px solid ${colors.primary[400]}55`,
      accentShadow: "0 18px 40px rgba(15, 23, 42, 0.18)",
      chipBackground: `linear-gradient(135deg, ${colors.primary[500]}33, ${colors.blueAccent[700]}45)`,
    }),
    [colors]
  );

  const metricCards = useMemo(() => [
    {
      label: t('totalDoctors'),
      value: totalDoctors,
      subtitle: t('totalDoctorsSubtitle'),
      icon: <LocalHospitalIcon sx={{ fontSize: 28 }} />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[800]} 0%, ${colors.greenAccent[800]} 100%)`,
    },
    {
      label: t('adminDoctors'),
      value: adminDoctors.length,
      subtitle: t('adminDoctorsSubtitle'),
      icon: <AdminPanelSettingsIcon sx={{ fontSize: 28 }} />,
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[900]} 100%)`,
    },
    {
      label: t('normalDoctors'),
      value: userDoctors.length,
      subtitle: t('normalDoctorsSubtitle'),
      icon: <Groups2OutlinedIcon sx={{ fontSize: 28 }} />,
      gradient: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
    },
    {
      label: t('totalPatients'),
      value: patients.length,
      subtitle: t('totalPatientsSubtitle'),
      icon: <PeopleAltOutlinedIcon sx={{ fontSize: 28 }} />,
      gradient: `linear-gradient(135deg, ${colors.redAccent[900]} 0%, ${colors.blueAccent[800]} 100%)`,
    },
  ], [totalDoctors, adminDoctors.length, userDoctors.length, patients.length, colors, t]);

  return (
    <TenantLayout
      contentSx={{
        ...tenantPanelContentSx,
        px: { xs: 2, md: 4, lg: 6 },
        py: { xs: 4, md: 6 },
        mt: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          borderRadius: 5,
          background: marketingPalette.hero,
          border: marketingPalette.accentBorder,
          boxShadow: marketingPalette.accentShadow,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 3, md: 4, lg: 5 },
            py: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.grey[100],
                letterSpacing: 0.4,
              }}
            >
              {t('tenantAnalytics')}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: `${colors.grey[100]}cc`, mt: 1, maxWidth: 520 }}
            >
              {t('tenantAnalyticsDescription')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              px: 3,
              py: 2.2,
              borderRadius: 4,
              background: marketingPalette.chipBackground,
              border: `1px solid ${colors.primary[400]}66`,
              boxShadow: `0 16px 32px rgba(15, 23, 42, 0.18)`,
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                width: 56,
                height: 56,
                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
                color: "#fff",
                boxShadow: `0 20px 38px rgba(15, 23, 42, 0.28)`,
                fontSize: 28,
                fontWeight: 700,
              }}
            >
              {totalDoctors + patients.length}
            </Avatar>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: `${colors.grey[100]}aa`,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                }}
              >
                {t('activeRecords')}
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: colors.grey[100] }}
              >
                {t('doctorsAndPatientsCombined')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 3, md: 4, lg: 5 }, pb: { xs: 3, md: 5 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: colors.blueAccent[500] }} />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography sx={{ color: colors.redAccent[500] }}>{error}</Typography>
            </Box>
          ) : (
            <>
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {metricCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.label}>
                <Box
                  sx={{
                    borderRadius: 4,
                    background: marketingPalette.surface,
                    border: `1px solid ${colors.primary[300]}44`,
                    boxShadow: `0 18px 34px rgba(148, 163, 184, 0.22)`,
                    display: "flex",
                    flexDirection: "column",
                    p: { xs: 2.5, md: 3 },
                    gap: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: `0 26px 48px rgba(82, 102, 255, 0.22)`,
                      borderColor: `${colors.blueAccent[700]}66`,
                    },
                  }}
                >
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 56,
                      height: 56,
                      background: card.gradient,
                      color: "#fff",
                      boxShadow: `0 18px 36px rgba(82, 102, 255, 0.24)`,
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: colors.grey[600],
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                      }}
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: colors.primary[900],
                        mt: 0.5,
                      }}
                    >
                      <CountUp start={0} end={card.value} duration={2} />
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[700], mt: 0.5 }}
                    >
                      {card.subtitle}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              ))}
            </Grid>

            {/* Analytics & Statistics - tenant grafik (panel renkleriyle uyumlu) */}
            <Box sx={{ mt: 5, mb: 2 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: colors.grey[100],
                }}
              >
                {t('dashboard:analyticsAndStatistics')}
              </Typography>
              <StatsFilters
                entity={selectedEntities}
                onEntityChange={setSelectedEntities}
                timeFilter={selectedTimeFilter}
                groupBy={selectedGroupBy}
                onTimeFilterChange={(value) => {
                  setSelectedTimeFilter(value);
                  if (value !== "custom") {
                    setCustomStartDate("");
                    setCustomEndDate("");
                  }
                  setSelectedGroupBy(statsService.getSuggestedGroupBy(value));
                }}
                onGroupByChange={setSelectedGroupBy}
                onRefresh={fetchStatsData}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onCustomStartDateChange={setCustomStartDate}
                onCustomEndDateChange={setCustomEndDate}
                dashboardMode
                entities={TENANT_ENTITIES}
              />
              {selectedEntities.length === 1 &&
                statsData?.data?.[selectedEntities[0]]?.summary && (
                  <StatsSummaryCards
                    summary={statsData.data[selectedEntities[0]].summary}
                    comparison={statsData.data[selectedEntities[0]].comparison}
                    entity={selectedEntities[0]}
                    metric="count"
                  />
                )}
              <Card
                sx={{
                  background: marketingPalette.surface,
                  borderRadius: 4,
                  border: `1px solid ${colors.primary[300]}44`,
                  boxShadow: `0 18px 34px rgba(148, 163, 184, 0.22)`,
                  mt: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: colors.primary[900],
                    }}
                  >
                    {selectedEntities.length === 1
                      ? t(`stats:entities.${selectedEntities[0]}`) || selectedEntities[0]
                      : selectedEntities
                          .map((e) => t(`stats:entities.${e}`) || e)
                          .join(", ")}{" "}
                    - {t("stats:metrics.count") || "Toplam Sayı"}
                  </Typography>
                  <StatsChart
                    data={statsData?.data}
                    loading={statsLoading}
                    error={statsError}
                    metric="count"
                    entity={
                      selectedEntities.length === 1 ? selectedEntities[0] : null
                    }
                    entities={selectedEntities}
                    files={[]}
                    period={selectedTimeFilter}
                    groupBy={
                      selectedGroupBy === "none"
                        ? statsService.getSuggestedGroupBy(selectedTimeFilter)
                        : selectedGroupBy
                    }
                  />
                </CardContent>
              </Card>
            </Box>
            </>
          )}
        </Box>
      </Box>
    </TenantLayout>
  );
}
